"use client";
import { Button } from "@cmp-src/primitives/button";
import { DeleteResourceModal } from "@/features/reserve/components/DeleteResourceModal";
import { EditMultipleTextInput } from "@/features/reserve/components/resources/EditMultipleTextInput";
import { SelectEditBooleanMultipleValues } from "@/features/reserve/components/resources/SelectEditBooleanMultipleValues";
import { LoadingSpinner } from "@gettactic/components/src/loadings/LoadingSpinner/LoadingSpinner";
import { api } from "@/lib/api/api";
import { cn } from "@gettactic/helpers/src/css/cn";
import {
  hasEnabledDeleteResource
} from "@gettactic/helpers/src/featureFlags";
import { replaceVarsBulkName } from "@/lib/utils";
import { EditResourceRequest, IResource } from "@gettactic/api";
import { useLoadingToast } from "@gettactic/helpers/src/hooks/toasts/useLoadingToast";
import { useAuthenticated } from "@gettactic/hooks";
import { TrashIcon } from "@heroicons/react/outline";
import { ScrollArea } from "@cmp-src/primitives/scroll-area";
import { BaseSyntheticEvent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

type Props = {
  resources: IResource[];
  mutate: () => void;
  notifyUpdateLabels?: (resWithLabels: IResource[]) => void;
  onSaved?: () => void;
};
export const RESOURCE_VALUE_NOT_EQUAL = "NOT_EQUAL";
export type ResourceValueNotEqualType = typeof RESOURCE_VALUE_NOT_EQUAL;

export type EditResourceMultipleForm = {
  bulkValues: {
    is_restricted: boolean;
    is_approvable: boolean;
    is_unavailable: boolean;
    is_walkup: boolean;
  }[];
  textBulkValues: {
    label: string;
    description: string;
    name: string;
  };
};

export function GeneralTabMultiple({
  resources,
  mutate,
  notifyUpdateLabels,
  onSaved
}: Props) {
  const toast = useLoadingToast({
    loadingMessage: "Applying changes..."
  });
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const [deleteModal, setDeleteModal] = useState([] as IResource[]);
  const defaultValues: EditResourceMultipleForm = {
    bulkValues: resources.map((x) => ({
      is_restricted: !x.is_open,
      is_approvable: x.is_approvable,
      is_unavailable: !x.is_available,
      is_walkup: !!x.is_walkup
    })),
    textBulkValues: {
      label: "",
      description: "",
      name: ""
    }
  };
  const { control, watch, handleSubmit, setValue } =
    useForm<EditResourceMultipleForm>({
      defaultValues
    });

  const bulkValuesLabel = watch("textBulkValues.label");
  useEffect(() => {
    if (!notifyUpdateLabels) {
      return;
    }
    const replacedLabels = resources.map((x, i) => ({
      ...x,
      label: bulkValuesLabel
        ? replaceVarsBulkName(bulkValuesLabel, resources.length - i - 1)
        : x.label
    }));
    notifyUpdateLabels(replacedLabels);
  }, [bulkValuesLabel, resources, notifyUpdateLabels]);
  const [loading, setLoading] = useState<boolean>(false);
  const onEditSubmit = async (
    data: EditResourceMultipleForm,
    event?: BaseSyntheticEvent<unknown, HTMLFormElement, HTMLFormElement>
  ) => {
    setLoading(true);
    toast.startLoading();
    let hasError = 0;

    for (let i = 0; i < resources.length; i++) {
      const resource = resources[i];
      const insertIndex = resources.length - i - 1;
      // Dedicated resources are booked for a single owner, not walked up
      // to — the two flags are mutually exclusive (see ResourceSettingsPanel).
      const isDedicated = !!resource.is_dedicated;
      const request: EditResourceRequest = {
        type: resource.type,
        parent_id: resource.parent_id,
        name:
          data.textBulkValues.name !== ""
            ? replaceVarsBulkName(data.textBulkValues.name, insertIndex)
            : resource.name,
        label:
          data.textBulkValues.label !== ""
            ? replaceVarsBulkName(data.textBulkValues.label, insertIndex)
            : resource.label,
        description:
          data.textBulkValues.description !== ""
            ? replaceVarsBulkName(data.textBulkValues.description, insertIndex)
            : resource.description,
        total_slots: resource.total_slots,
        maximum_slots_per_user: resource.maximum_slots_per_user,
        is_open: !data.bulkValues[i].is_restricted,
        is_approvable: data.bulkValues[i].is_approvable,
        is_available: !data.bulkValues[i].is_unavailable,
        // TODO: Add this field to the UI
        is_shareable: resource.is_shareable,
        is_walkup: isDedicated ? false : data.bulkValues[i].is_walkup,
        weight: resource.weight,
        map_url: resource.map_url
      };
      const { ok } = await api.client.organizations.resourcePut(
        resource.id,
        request
      );
      setLoading(false);

      if (!ok) {
        hasError++;
      }
      mutate();
    }
    if (hasError === 0) {
      onSaved?.();
      toast.success("Changes saved");
    } else {
      toast.error("Sorry we couldn't save all the changes");
    }
  };

  return (
    <div>
      <DeleteResourceModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
      />

      <form
        id="resourceSettingsPanel"
        className="flex flex-auto flex-col flex-nowrap overflow-hidden"
        onSubmit={handleSubmit(onEditSubmit)}
      >
        <div className="">
          <ScrollArea className="h-full">
            <h4 className="my-4 text-lg font-bold text-secondary">
              Edit multiple resources
            </h4>
            <div className="mt-4 flex flex-col space-y-4 px-2">
              <EditMultipleTextInput
                name="name"
                defaultValues={defaultValues}
                control={control}
                setValue={setValue}
                label="Name"
              />
              <div>
                <EditMultipleTextInput
                  name="label"
                  defaultValues={defaultValues}
                  control={control}
                  setValue={setValue}
                  label="Label"
                />
              </div>
              <EditMultipleTextInput
                name="description"
                defaultValues={defaultValues}
                control={control}
                setValue={setValue}
                label="Description"
              />
              <SelectEditBooleanMultipleValues
                name="is_restricted"
                defaultValues={defaultValues}
                control={control}
                resources={resources}
                setValue={setValue}
                label="Assigned"
                help="When this box is checked, only assigned teams &amp; users will be able to make reservations."
              />

              <SelectEditBooleanMultipleValues
                name="is_approvable"
                defaultValues={defaultValues}
                control={control}
                resources={resources}
                setValue={setValue}
                label="Approval Required"
                help="When this box is checked, reservations will need to be approved by an admin or team manager."
              />

              <SelectEditBooleanMultipleValues
                name="is_unavailable"
                defaultValues={defaultValues}
                control={control}
                resources={resources}
                setValue={setValue}
                label="Make unavailable"
                help="When this box is checked, no new reservations can be made."
                color="red"
              />

              <SelectEditBooleanMultipleValues
                name="is_walkup"
                defaultValues={defaultValues}
                control={control}
                resources={resources}
                setValue={setValue}
                label="Walk-up"
                help="Walk-up resources can't be reserved from the app. Occupancy is managed externally via the API."
              />
            </div>
          </ScrollArea>
        </div>
        <div className="my-4 flex flex-nowrap gap-3">
          <Button
            className="w-full sm:w-auto sm:flex-1"
            variant="default"
            size="default"
            type="submit"
            form="resourceSettingsPanel"
            disabled={loading}
          >
            {loading ? <LoadingSpinner color="text-white" /> : <>Save</>}
          </Button>
          {hasEnabledDeleteResource(authenticatedUser) ? (
            <Button
              className="w-full flex-none sm:w-auto"
              variant="destructive"
              size="default"
              disabled={loading}
              onClick={(ev) => {
                ev.preventDefault();
                setDeleteModal(resources);
              }}
            >
              <TrashIcon className="h-6 w-6" />
            </Button>
          ) : null}
        </div>
      </form>
    </div>
  );
}
